// Phase 4 Task 9 — PieceSpriteMount @pixi/react adapter for chess pieces.
//
// Bridges React's declarative pixi-react surface with the imperative
// PieceSprite class. Mirrors LetterCubeSpriteMount but uses a simplified
// GLIDE-FROM-NOW approach (no $boardReanchor gating) because the chess view
// is mutually exclusive with the letter view and does not show the
// flop/river reveal cinematic.
//
// Design (mirrors LetterCubeSpriteMount):
//   - <pixiContainer ref={...}> → addChild the imperative PieceSprite once.
//   - First ref pass: construct PieceSprite + addChild + snap position.
//   - Subsequent renders: call sprite.updatePiece() + sprite.updatePosition()
//     so (a) the draw is diff-gated and (b) a position change glides.
//   - Unmount: ref called with null → Pixi Container.destroy cascade tears
//     down the PieceSprite, which drains its tween (Pitfall 5 lock).

import type { Container as PixiContainer } from 'pixi.js'
import type { JSX } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import type { Group } from 'tweedle.js'
import type { Kind } from '../layers/PieceLayer'
import { PieceSprite } from './PieceSprite'

export interface PieceSpriteProps {
  kind: Kind
  ownerSlot: 0 | 1
  fill: number
  glow: number
  magic: number
  empowered: boolean
}

export interface PieceSpriteMetaMountProps extends PieceSpriteProps {
  /** Absolute pixel x within the PieceLayer container. */
  x: number
  /** Absolute pixel y within the PieceLayer container. */
  y: number
  reducedMotion: boolean
  tweenGroup: Group
  /** Stable label used as the Pixi Container's `label` prop (for devtools). */
  label: string
}

/**
 * @pixi/react adapter: mounts a PieceSprite inside a pixi-react
 * `<pixiContainer>` and bridges React prop updates to the sprite's
 * updatePiece() / updatePosition() surfaces.
 */
export function PieceSpriteMount(
  props: PieceSpriteMetaMountProps,
): JSX.Element {
  const spriteRef = useRef<PieceSprite | null>(null)

  // Latest props for the construct-once ref callback to read at attach time.
  // The ref callback must be STABLE so it cannot close over per-render props.
  const latestProps = useRef(props)
  latestProps.current = props

  // STABLE ref callback — construct PieceSprite exactly ONCE on attach.
  // An unstable inline callback would be re-invoked with null+node on every
  // render, stacking sprites (same pitfall as LetterCubeSpriteMount).
  const attachRef = useCallback((container: PixiContainer | null): void => {
    if (!container) {
      // Real unmount: drop our cached ref. PieceSprite is destroyed via
      // the parent container's destroy cascade (Pixi Container.destroy
      // {children:true}) → PieceSprite.destroy() drains the tween
      // (Pitfall 5 lock).
      spriteRef.current = null
      return
    }
    if (spriteRef.current) return // already constructed — never reconstruct
    const p = latestProps.current
    const sprite = new PieceSprite({
      kind: p.kind,
      ownerSlot: p.ownerSlot,
      fill: p.fill,
      glow: p.glow,
      magic: p.magic,
      empowered: p.empowered,
      tweenGroup: p.tweenGroup,
    })
    // First mount: snap to initial position (no glide on construction).
    sprite.x = p.x
    sprite.y = p.y
    // Mark as having initial position so subsequent updatePosition calls
    // can distinguish "moved" from "first set".
    sprite.updatePosition(p.x, p.y, p.reducedMotion)
    container.addChild(sprite)
    spriteRef.current = sprite
  }, [])

  // Per-render diff: update piece visuals and position. Runs immediately
  // after the ref attaches the sprite on first mount, then on any prop change.
  //
  // props.reducedMotion is intentionally absent from deps: it is captured at
  // construction and PieceLayer re-keys the whole layer on reducedMotion flip
  // (mirroring LetterLayer's pattern).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reducedMotion is immutable per mount lifetime — PieceLayer re-keys the layer on flip
  useEffect(() => {
    const sprite = spriteRef.current
    if (sprite === null) return

    sprite.updatePiece({
      kind: props.kind,
      ownerSlot: props.ownerSlot,
      fill: props.fill,
      glow: props.glow,
      magic: props.magic,
      empowered: props.empowered,
    })

    sprite.updatePosition(props.x, props.y, props.reducedMotion)
  }, [
    props.kind,
    props.ownerSlot,
    props.fill,
    props.glow,
    props.magic,
    props.empowered,
    props.x,
    props.y,
  ])

  return <pixiContainer label={props.label} ref={attachRef} />
}
